/**
 * PRD.289 — Pharmacy per-medicine Stock Card drawer.
 *
 * Clicking a queue row's Medicine opens this read-only right drawer showing
 * the stock-movement ledger scoped to that single inventory item. It reuses
 * the existing Stock Card data layer (`qStockCardMovementsByItem` +
 * `computeRunningBalances` + `deriveStockCardReference`) so the numbers match
 * the full Stock Card page exactly — no duplicate ledger logic.
 *
 * The Reference column is rendered as plain text here (no navigation): the
 * pharmacist stays inside the Pharmacy module (never routes to `/patient/:id`).
 */
import { useMemo, type CSSProperties } from 'react';
import { Drawer } from '@helios/ui/overlay';
import {
  computeRunningBalances,
  deriveStockCardReference,
  qStockCardMovementsByItem,
  type StockCardMovementRow,
  type StockCardMovementVM,
} from '@helios/data-access';
import { usePowerSyncQuery } from '../../adapters/powersync';
import { useBrandTokens } from '../../shell/shared';

export interface PharmacyStockCardContext {
  itemId: string;
  itemName: string | null;
  sku: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context: PharmacyStockCardContext | null;
}

function formatNum(value: number | null | undefined, fallback = '—'): string {
  if (value == null) return fallback;
  if (Number.isInteger(value)) return value.toString();
  return value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 4 });
}

function movementTypeLabel(value: string | null): string {
  switch (value) {
    case 'adjustment_add':
      return 'Adj — Add';
    case 'adjustment_deduct':
      return 'Adj — Deduct';
    case 'delivery_received':
    case 'direct_delivery_received':
      return 'Delivery';
    case 'delivery_reversal':
    case 'direct_delivery_reversal':
      return 'Delivery Reversal';
    case 'pharmacy_dispense':
      return 'Dispense';
    case 'product_release':
      return 'Product Release';
    case 'used_supply':
      return 'Used Supply';
    case 'used_supply_reversal':
      return 'Used Supply Reversal';
    case 'opening_balance':
      return 'Opening';
    case 'correction':
      return 'Correction';
    default:
      return value ?? '—';
  }
}

export function PharmacyStockCardDrawer({ open, onOpenChange, context }: Props) {
  const brandTokens = useBrandTokens();
  const itemId = context?.itemId ?? '';

  const { data: movementRows = [] } = usePowerSyncQuery<StockCardMovementRow>(
    qStockCardMovementsByItem(itemId),
  );

  // Reuse the shared running-balance computation so per-movement balances
  // match the Stock Card page. The query is already item-scoped.
  const rows: StockCardMovementVM[] = useMemo(
    () => computeRunningBalances(movementRows, 'desc'),
    [movementRows],
  );

  // Summary is derived from the same item-scoped movement rows (matching the
  // Stock Card page) so the totals can never diverge from the visible ledger.
  const summary = useMemo(() => {
    let totalIn = 0;
    let totalOut = 0;
    for (const r of rows) {
      totalIn += r.quantity_in ?? 0;
      totalOut += r.quantity_out ?? 0;
    }
    return { total_in: totalIn, total_out: totalOut, current_stock: totalIn - totalOut };
  }, [rows]);
  const unit = rows.find((r) => r.unit)?.unit ?? '';

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="Stock Card"
      description={
        context
          ? `${context.itemName ?? 'Medicine'}${context.sku ? ` · SKU ${context.sku}` : ''}`
          : undefined
      }
      portalStyle={brandTokens as CSSProperties}
      width="min(720px, 94vw)"
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-4)',
          padding: 'var(--sp-5)',
        }}
      >
        {/* Summary — current stock / total in / total out for this item. */}
        {rows.length > 0 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 'var(--sp-3)',
            }}
          >
            <SummaryCard
              label="Current Stock"
              value={`${formatNum(summary.current_stock)}${unit ? ` ${unit}` : ''}`}
              accent="primary"
            />
            <SummaryCard label="Total In" value={formatNum(summary.total_in)} accent="positive" />
            <SummaryCard label="Total Out" value={formatNum(summary.total_out)} accent="negative" />
          </div>
        )}

        {rows.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: 'var(--sp-8)',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
              border: '2px dashed var(--border)',
              borderRadius: 'var(--radius-lg)',
            }}
          >
            No stock movements recorded for this medicine yet.
          </div>
        ) : (
          <div
            style={{
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-lg)',
              overflow: 'hidden',
            }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <Th>Date</Th>
                  <Th>Batch</Th>
                  <Th>Source</Th>
                  <Th>Type</Th>
                  <Th align="right">In</Th>
                  <Th align="right">Out</Th>
                  <Th align="right">Balance</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const ref = deriveStockCardReference(row);
                  return (
                    <tr key={row.movement_id} style={{ borderTop: '1px solid var(--border)' }}>
                      <Td>
                        <span style={{ color: 'var(--text-muted)' }}>
                          {row.movement_date ?? '—'}
                        </span>
                      </Td>
                      <Td>
                        <div>
                          {row.batch_number ??
                            (row.inventory_batch_id ? row.inventory_batch_id.slice(0, 8) : '—')}
                        </div>
                        {row.expiry_date && (
                          <div
                            style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}
                          >
                            Exp {row.expiry_date}
                          </div>
                        )}
                      </Td>
                      <Td>
                        <div>{row.source_label}</div>
                        {/* Reference as plain text — no navigation out of Pharmacy. */}
                        <div
                          style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}
                        >
                          {ref.primary_label}
                          {ref.secondary_label ? ` · ${ref.secondary_label}` : ''}
                        </div>
                      </Td>
                      <Td>
                        <span style={{ color: 'var(--text-muted)' }}>
                          {movementTypeLabel(row.movement_type)}
                        </span>
                      </Td>
                      <Td align="right">
                        <span
                          style={{
                            fontWeight: 'var(--font-weight-medium)',
                            color:
                              row.quantity_in != null && row.quantity_in > 0
                                ? 'var(--color-success-text)'
                                : 'var(--text-muted)',
                          }}
                        >
                          {row.quantity_in != null && row.quantity_in > 0
                            ? `+${formatNum(row.quantity_in)}`
                            : '—'}
                        </span>
                      </Td>
                      <Td align="right">
                        <span
                          style={{
                            fontWeight: 'var(--font-weight-medium)',
                            color:
                              row.quantity_out != null && row.quantity_out > 0
                                ? '#991b1b'
                                : 'var(--text-muted)',
                          }}
                        >
                          {row.quantity_out != null && row.quantity_out > 0
                            ? `-${formatNum(row.quantity_out)}`
                            : '—'}
                        </span>
                      </Td>
                      <Td align="right">
                        <span
                          style={{ fontWeight: 'var(--font-weight-semi)', color: 'var(--text)' }}
                        >
                          {formatNum(row.running_balance)}
                        </span>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Drawer>
  );
}

function SummaryCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent: 'primary' | 'positive' | 'negative';
}) {
  const accentColor =
    accent === 'positive'
      ? 'var(--color-success-text)'
      : accent === 'negative'
        ? '#991b1b'
        : 'var(--text)';
  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--sp-3) var(--sp-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 'var(--font-weight-semi)',
          color: accentColor,
        }}
      >
        {value}
      </span>
    </div>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children?: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <th
      style={{
        padding: '10px 10px',
        textAlign: align,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-semi)',
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg-surface-2, #fafafa)',
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = 'left',
}: {
  children?: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <td
      style={{
        padding: '10px 10px',
        textAlign: align,
        fontSize: 'var(--font-size-sm)',
        color: 'var(--text)',
        verticalAlign: 'top',
      }}
    >
      {children}
    </td>
  );
}
